import React from 'react';
import {Button, Card, Col, DatePicker, Form, Row, Spin} from 'antd';
import dayjs from 'dayjs';
import _ from 'lodash';

import DeploymentSelect from '../DeploymentSelect';
import EngageCurrency from '../billings/EngageCurrency';
import {useAvailableDeployments} from '../../hooks/useAvailableDeployments';
import {useUpdateBookingMutation} from '../../store/api/bookingsApi';
import {BaseError} from '../../types/apiTypes';
import {BookingDetailsRequestDataType, BookingUpdateFormProps} from '../../types/bookingTypes';
import {useAppSelector} from '../../hooks/useStore';
import {selectIsStaff} from '../../store/slices/userSlice';

import {
    checkEndTimeIsValid,
    checkStartTimeBeforeEndTime,
    checkStartTimeFuture,
    getBookingIncrement,
    getDistanceUnit,
    dstOffSetDateCheck,
} from '../../helpers/booking';
import {messageType, showMessages} from '../../helpers/message';
import {dateTimeFormat} from '../../lib/time';
import {formatTimestamp, getTimeDiff} from '../../helpers/misc';
import {
    bookingDetailsHaveSuccessfullyBeenUpdated,
    cancelledBookingCannotBeModified,
    nothingToUpdate,
    unsupportedBookingType,
} from '../../helpers/text';

import {formInCardLayout, formItemFullWidth} from '../../assets/styles';
import {errorRed, successGreen} from '../../assets/colors';
import './BookingUpdateForm.less';

const BookingUpdateForm = ({
    bookingID,
    bookingDetails,
    bookingDeploymentDetails,
    isEstimatedCost,
    isCancelledBooking,
    totalCost,
}: BookingUpdateFormProps) => {
    const [form] = Form.useForm();

    const [newDeploymentID, setNewDeploymentID] = React.useState<number | null>(null);
    const [previousDeploymentID, setPreviousDeploymentID] = React.useState<number | null>(null);

    const hasStarted = dayjs().isAfter(bookingDeploymentDetails.started_at);
    const hasEnded = dayjs().isAfter(bookingDeploymentDetails.ended_at);

    const [updateBooking, {
        isSuccess,
        isError,
        error,
        isLoading: isUpdateLoading,
        data: updateData,
        reset
    }] = useUpdateBookingMutation();
    const {allowedStartTime, allowedEndTime, availabilityDataError} = useAvailableDeployments({
        deploymentID: bookingDeploymentDetails.deployment_id,
        bookingDeploymentDetails,
        isCancelledBooking,
        isUpdateLoading,
    });

    const resetTimeToOriginal = React.useCallback(() => {
        form.setFieldsValue({
            'startTime': dayjs(bookingDeploymentDetails.started_at).tz(),
            'endTime': dayjs(bookingDeploymentDetails.ended_at).tz(),
        });
    }, [bookingDeploymentDetails.ended_at, bookingDeploymentDetails.started_at, form]);

    React.useEffect(() => {
        resetTimeToOriginal();
    }, [resetTimeToOriginal]);

    React.useEffect(() => {
        if (availabilityDataError) showMessages((availabilityDataError as BaseError).data?.message, messageType.error);
    }, [availabilityDataError]);

    React.useEffect(() => {
        if (isSuccess) {
            showMessages(bookingDetailsHaveSuccessfullyBeenUpdated, messageType.success);
            setPreviousDeploymentID(newDeploymentID);
            setNewDeploymentID(null);
            reset();
        }
        if (updateData) {
            updateData.warnings?.forEach((warning: string) => {
                showMessages(warning, messageType.warn);
            });
        }
        if (isError) {
            resetTimeToOriginal();
            showMessages((error as BaseError).data?.message, messageType.error);
            reset();
        }
    }, [error, isError, isSuccess, newDeploymentID, reset, resetTimeToOriginal, updateData]);

    const bookingIncrement = getBookingIncrement();
    const isStaff = useAppSelector(selectIsStaff);
    const mobileMode = window.innerWidth <= 768;

    // Disable dates earlier than today in date picker.
    const disabledDate = (input: dayjs.Dayjs) => {
        return input < dayjs().tz().subtract(1, 'days').endOf('day');
    };

    const selectedVehicleInfo = {
        deploymentID: bookingDeploymentDetails.deployment_id,
        deployableDescription: bookingDetails.deployable.description,
        locationDescription: bookingDetails.booking_location.description,
    };

    const onStartTimeChange = (newStart: dayjs.Dayjs | null) => {
        if (newStart) {
            const [start, end] = dstOffSetDateCheck({
                start: newStart,
                end: form.getFieldValue('endTime'),
                isStart: true,
                format: dateTimeFormat,
            });
            form.setFieldsValue({'startTime': start, 'endTime': end});
        }
    };

    const onEndTimeChange = (newEnd: dayjs.Dayjs | null) => {
        if (newEnd) {
            const [start, end] = dstOffSetDateCheck({
                start: form.getFieldValue('startTime'),
                end: newEnd,
                isStart: false,
                format: dateTimeFormat,
            });
            form.setFieldsValue({'startTime': start, 'endTime': end});
        }
    };

    const isSelectorDisabled = (disableCondition: boolean) => {
        if (isStaff || isCancelledBooking) {
            // Disable when booking is cancelled except for staff.
            return !isStaff;
        }
        return disableCondition;
    };

    // Sanity check and api call on form submit.
    const handleFinish = async () => {
        let requestData: BookingDetailsRequestDataType = {};
        const currentMinute = dayjs().tz().startOf('minute');

        const newStartedAt = form.getFieldValue('startTime');
        const newEndedAt = form.getFieldValue('endTime');
        const oldStartedAt = dayjs(bookingDeploymentDetails.started_at).tz();
        const oldEndedAt = dayjs(bookingDeploymentDetails.ended_at).tz();
        const bookingType = bookingDeploymentDetails.type;

        if (!isStaff && isCancelledBooking) {
            showMessages(cancelledBookingCannotBeModified, messageType.error);
            resetTimeToOriginal();
            return;
        }

        // Put checks in here that are specific to only that booking type.
        switch (bookingType) {
            case 'open_return':
            case 'set_return':
                if (!checkStartTimeBeforeEndTime(newStartedAt, newEndedAt)) {
                    resetTimeToOriginal();
                    return;
                }
                // If there is change to ended_at.
                if (!newEndedAt.isSame(oldEndedAt)) {
                    if (isStaff || checkEndTimeIsValid(newEndedAt, oldEndedAt, currentMinute)) {
                        requestData['ended_at'] = formatTimestamp(newEndedAt);
                    } else {
                        resetTimeToOriginal();
                        return;
                    }
                }
                break;
            default:
                showMessages(unsupportedBookingType, messageType.error);
                return;
        }

        // If there is change to started_at.
        if (!newStartedAt.isSame(oldStartedAt)) {
            if (isStaff || checkStartTimeFuture(newStartedAt, currentMinute)) {
                requestData['started_at'] = formatTimestamp(newStartedAt);
            } else {
                resetTimeToOriginal();
                return;
            }
        }

        if (isStaff || (newDeploymentID !== null && !hasStarted)) {
            if (newDeploymentID && newDeploymentID !== previousDeploymentID) {
                requestData['deployment_id'] = newDeploymentID;
            }
        }

        // If empty, nothing to update.
        if (Object.keys(requestData).length === 0) {
            showMessages(nothingToUpdate, messageType.error);
            return;
        }

        await updateBooking({bookingID, requestData});
    };

    const commonDatePickerProps = {
        showTime: {
            minuteStep: Number(bookingIncrement) / 60,
            use12Hours: true,
            format: "hh:mm A",
        },
        disabledDate: isStaff ? undefined : disabledDate,
        format: dateTimeFormat,
        allowClear: false,
        inputReadOnly: mobileMode,
        style: formItemFullWidth,
    };

    return (
        <Card title="What You Booked" className="booking-manage-details-card">
            <Spin spinning={isUpdateLoading}>
                <Col className="booking-update-details">
                    <Row>
                        <Col md={10} xs={12}><strong>Booking Status:</strong></Col>
                        <Col md={14} xs={12}
                             style={{color: bookingDeploymentDetails.state === 'CANCELLED' ? errorRed : successGreen}}>
                            {bookingDeploymentDetails.state.toUpperCase()}
                        </Col>
                    </Row>
                    <Row>
                        <Col md={10} xs={12}><strong>Return Type:</strong></Col>
                        <Col md={14} xs={12}>
                            {_.startCase(bookingDeploymentDetails.type)}
                        </Col>
                    </Row>
                    <Row>
                        <Col md={10} xs={12}><strong>{isEstimatedCost ? 'Estimated Cost' : 'Total Cost'}:</strong></Col>
                        <Col md={14} xs={12}><EngageCurrency amount={totalCost}/></Col>
                    </Row>
                    <Row>
                        <Col md={10} xs={12}><strong>Duration:</strong></Col>
                        <Col md={14}
                             xs={12}>{getTimeDiff(bookingDeploymentDetails.started_at, bookingDeploymentDetails.ended_at)}</Col>
                    </Row>
                    <Row>
                        <Col md={10} xs={12}><strong>Distance:</strong></Col>
                        <Col md={14} xs={12}>{bookingDetails.distance ?? 'N/A'} {getDistanceUnit().toLowerCase()}</Col>
                    </Row>
                </Col>
                <Form
                    {...formInCardLayout}
                    className="booking-update-form"
                    name="update-booking"
                    form={form}
                    onFinish={handleFinish}
                    initialValues={{
                        'startTime': dayjs(bookingDeploymentDetails.started_at).tz(),
                        'endTime': dayjs(bookingDeploymentDetails.ended_at).tz(),
                    }}
                >
                    <Form.Item
                        name="startTime"
                        className="update-form-picker"
                        label="Start Date & Time"
                        rules={[{type: 'object', required: true}]}
                        extra={<div className={allowedStartTime?.className}>{allowedStartTime?.message}</div>}
                    >
                        <DatePicker
                            {...commonDatePickerProps}
                            onChange={onStartTimeChange}
                            disabled={isSelectorDisabled(hasStarted)}
                        />
                    </Form.Item>
                    <Form.Item
                        name="endTime"
                        className="update-form-picker"
                        label="End Date & Time"
                        rules={[{type: 'object', required: true}]}
                        extra={<div className={allowedEndTime?.className}>{allowedEndTime?.message}</div>}
                    >
                        <DatePicker
                            {...commonDatePickerProps}
                            onChange={onEndTimeChange}
                            disabled={isSelectorDisabled(hasEnded)}
                        />
                    </Form.Item>
                    <Form.Item label="Vehicle" className="update-form-picker" required>
                        <DeploymentSelect
                            {...selectedVehicleInfo}
                            startedAt={bookingDeploymentDetails.started_at}
                            endedAt={bookingDeploymentDetails.ended_at}
                            onChange={(value) => setNewDeploymentID(value)}
                            disabled={isSelectorDisabled(hasStarted) || isUpdateLoading}
                        />
                    </Form.Item>
                    <Form.Item className="update-booking-submit-button">
                        <Button type="primary" htmlType="submit" disabled={isCancelledBooking && !isStaff}>
                            Update Booking
                        </Button>
                    </Form.Item>
                </Form>
            </Spin>
        </Card>
    );
};

export default BookingUpdateForm;
